import React, { useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import Dialog from '../../../../../../common/components/ConfirmationDialog/Dialog';
import { useTranslations } from '../../../../../../contexts/LocalizationContext';
import { AddEditQuickContactModalBody } from '../../../../common/AddEditQuickContactModalBody/AddEditQuickContactModalBody';
import { QuickContactType } from '../../../../model/enums/quick-contact-type.enum';
import { CreateNewContactForm } from './form/create-new-contact-form';
import { useAddQuickContactsFormValidation } from './form/use-add-quick-contacts-form-validation';

type AddNewContactsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (form: CreateNewContactForm, onSuccess: () => void) => void;
  isLoading: boolean;
};

export const AddNewContactsModal = ({ isOpen, onClose, onSubmit, isLoading }: AddNewContactsModalProps) => {
  const translations = useTranslations();
  const validationSchema = useAddQuickContactsFormValidation();
  const confirmCancelRef = useRef(null);

  const methods = useForm<CreateNewContactForm>({
    defaultValues: {
      type: QuickContactType.USER,
      nickname: '',
      street: '',
      streetNumber: '',
      postalCode: '',
      country: undefined,
      city: undefined,
      selectedRestaurants: [],
    },
    resolver: yupResolver(validationSchema),
  });

  const handleClose = () => {
    methods.reset({
      type: QuickContactType.USER,
      nickname: undefined,
      street: undefined,
      streetNumber: undefined,
      postalCode: undefined,
      country: undefined,
      city: undefined,
      selectedRestaurants: [],
    });
    onClose();
  };

  const handleSubmit = (form: CreateNewContactForm) => {
    onSubmit(form, handleClose);
  };

  return (
    <Dialog
      leastDestructiveRef={confirmCancelRef}
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      dialogHeader={translations('quick_contacts_add_new_contact_modal_header')}
      dialogFooter={<></>}
    >
      <FormProvider {...methods}>
        <AddEditQuickContactModalBody onSubmit={handleSubmit} isLoading={isLoading} />
      </FormProvider>
    </Dialog>
  );
};
